import { Matrix4, Vector3 } from 'three';
import type { Vec3 } from '../../geo/types';
import type { SiteModel } from '../../site/types';
import { CARGO, cargoHeadingAt, cargoX } from '../life/cargoPlan';
import { alongLine, lineOf, type OnLine, type WalkLine } from '../life/crowd/walkLine';
import { floorHeight } from './boatSection';
import { hullStations } from './hull';
import { floatingHeight } from './shipMeshes';
import { deckAt, formedTemplate } from './shipForm';
import { SHIP_TEMPLATES } from './shipTemplates';
import { tradeEase, tradeMotionAt, type TradeTimetable } from './tradeMotion';
import type { HarbourTrade } from './tradePlan';
import { TRADE_SCALE } from './tradeScale';

const wrap = (a: number) => Math.atan2(Math.sin(a), Math.cos(a));

function boatHeading(line: WalkLine, distance: number) {
  const a: OnLine = { x: 0, y: 0, ground: 0, heading: 0 },
    b = { ...a };
  alongLine(line, Math.max(0, distance - 4) / line.length, a);
  alongLine(line, Math.min(line.length, distance + 4) / line.length, b);
  return Math.atan2(b.y - a.y, b.x - a.x) + Math.PI;
}

/** Cargo follows a continuous hoist path between real carrier frames; it never snaps at a handover. */
function hoisted(a: Vec3, b: Vec3, progress: number, height: number): Vec3 {
  if (progress < 0.3) return [a[0], a[1] + (height - a[1]) * tradeEase(progress / 0.3), a[2]];
  if (progress < 0.7) {
    const q = tradeEase((progress - 0.3) / 0.4);
    return [a[0] + (b[0] - a[0]) * q, height, a[2] + (b[2] - a[2]) * q];
  }
  return [b[0], height + (b[1] - height) * tradeEase((progress - 0.7) / 0.3), b[2]];
}

export function tradeLines(plan: HarbourTrade, site: SiteModel) {
  const water = lineOf(plan.water.map(([x, y]) => [x, y, 0] as const));
  const cart = lineOf(
    plan.cart.map(([x, y]) => [x, y, site.queries.groundHeightAt([x, y])] as const)
  );
  return { water, cart };
}

export function tradeFrames(
  plan: HarbourTrade,
  lines: ReturnType<typeof tradeLines>,
  timetable: TradeTimetable,
  site: SiteModel
) {
  const at: OnLine = { x: 0, y: 0, ground: 0, heading: 0 };
  const boat = new Matrix4(),
    cart = new Matrix4(),
    local = new Matrix4();
  const point = new Vector3();
  const stations = hullStations(SHIP_TEMPLATES.praam);
  const floor = floorHeight(stations[stations.length >> 1]!) + 0.06;
  const cargoAt = (frame: Matrix4, x: number, y: number): Vec3 => {
    point.set(x, y, 0).applyMatrix4(frame);
    return [point.x, point.y, point.z];
  };
  const float = floatingHeight({ type: 'praam' });
  const waterFrame = (distance: number, turn: number) => {
    alongLine(lines.water, distance / lines.water.length, at);
    return new Matrix4()
      .makeRotationY(boatHeading(lines.water, distance) + turn)
      .setPosition(at.x, float, -at.y);
  };
  const cartFrame = (distance: number) => {
    alongLine(
      lines.cart,
      (((distance % lines.cart.length) + lines.cart.length) % lines.cart.length) /
        lines.cart.length,
      at
    );
    return new Matrix4()
      .makeRotationY(cargoHeadingAt(lines.cart, distance))
      .setPosition(at.x, at.ground, -at.y);
  };
  const shipTemplate = formedTemplate(SHIP_TEMPLATES[plan.ship.type]);
  const berth = plan.water[0]!;
  const side = Math.sign(
    -Math.sin(plan.ship.heading) * (berth[0] - plan.ship.position[0]) +
      Math.cos(plan.ship.heading) * (berth[1] - plan.ship.position[1])
  );
  const shipMatrix = new Matrix4()
    .makeRotationY(plan.ship.heading)
    .setPosition(plan.ship.position[0], 0, -plan.ship.position[1]);
  point
    .set(0, deckAt(shipTemplate, 0.5), -side * shipTemplate.beam * 0.35)
    .applyMatrix4(shipMatrix);
  const sea: Vec3 = [point.x, point.y, point.z];
  const fromBoat = cargoAt(waterFrame(0, 0), 0, floor);
  const dockBoat = cargoAt(waterFrame(lines.water.length, 0), 0, floor);
  const axle = cargoX(CARGO.axleX, 0.82);
  const fromCart = cargoAt(cartFrame(0), axle, 0.7);
  const endCart = cargoAt(cartFrame(plan.arrival), axle, 0.7);
  const ground = site.queries.groundHeightAt(plan.scale);
  const scaleFrame = new Matrix4()
    .makeRotationY(plan.heading)
    .setPosition(plan.scale[0], ground, -plan.scale[1]);
  const pan = (angle: number): Vec3 => {
    point
      .set(
        TRADE_SCALE.arm * Math.cos(angle),
        TRADE_SCALE.pivot +
          TRADE_SCALE.arm * Math.sin(angle) -
          TRADE_SCALE.chain +
          TRADE_SCALE.panTop,
        0
      )
      .applyMatrix4(scaleFrame);
    return [point.x, point.y, point.z];
  };
  const stored: Vec3 = [plan.store[0], site.queries.groundHeightAt(plan.store), -plan.store[1]];
  return {
    floor,
    sea,
    stored,
    shipMatrix,
    scaleFrame,
    at(seconds: number) {
      const motion = tradeMotionAt(timetable, seconds);
      boat.copy(waterFrame(motion.boatDistance, motion.boatTurn));
      cart.copy(cartFrame(motion.cartDistance));
      const scaleAngle = motion.stage === 'weigh' ? -0.12 * Math.sin(Math.PI * motion.progress) : 0;
      let cargo: Vec3;
      const q = motion.progress;
      if (motion.stage === 'load') cargo = hoisted(sea, fromBoat, q, Math.max(sea[1] + 2.5, 3));
      else if (motion.stage === 'sail') cargo = cargoAt(boat, 0, floor);
      else if (motion.stage === 'unload') cargo = hoisted(dockBoat, fromCart, q, 3);
      else if (motion.stage === 'deliver') cargo = cargoAt(cart, axle, 0.7);
      else if (motion.stage === 'lift') cargo = hoisted(endCart, pan(0), q, ground + 2.8);
      else if (motion.stage === 'weigh') cargo = pan(scaleAngle);
      else if (motion.stage === 'store') cargo = hoisted(pan(0), stored, q, ground + 2.8);
      else cargo = stored;
      const cartHeading = cargoHeadingAt(lines.cart, motion.cartDistance);
      const boatYaw = boatHeading(lines.water, motion.boatDistance) + motion.boatTurn;
      let cargoHeading = plan.heading;
      if (motion.stage === 'load')
        cargoHeading =
          plan.ship.heading + wrap(boatHeading(lines.water, 0) - plan.ship.heading) * tradeEase(q);
      else if (motion.stage === 'sail') cargoHeading = boatYaw;
      else if (motion.stage === 'unload')
        cargoHeading =
          boatHeading(lines.water, lines.water.length) +
          wrap(cargoHeadingAt(lines.cart, 0) - boatHeading(lines.water, lines.water.length)) *
            tradeEase(q);
      else if (motion.stage === 'deliver') cargoHeading = cartHeading;
      else if (motion.stage === 'lift')
        cargoHeading = cartHeading + wrap(plan.heading - cartHeading) * tradeEase(q);
      local
        .makeScale(0.82, 1, 1)
        .setPosition(CARGO.gripX * (1 - 0.82), 0, 0)
        .premultiply(cart);
      return {
        ...motion,
        boat,
        cart,
        body: local,
        cargo,
        cargoHeading,
        scaleAngle,
        boatYaw,
        cartHeading,
      };
    },
  };
}
